import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Loader,
  Center,
  Alert,
  Image,
  Card,
  SegmentedControl,
  Select,
  Divider,
  NumberInput,
  Box,
  NativeSelect,
  UnstyledButton,
  TextInput,
  Checkbox,
  ActionIcon,
} from '@mantine/core';
import { IconAlertCircle, IconX, IconPhoto } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../context/AuthContext';
import { reviewsService } from '../../services/reviews';
import { tagsService } from '../../services/tags';
import { HashtagTextarea } from '../../components/hashtags/HashtagTextarea';
import { StarRating } from '../../components/reviews';
import { ReviewVisibility } from '../../types';
import type { ReviewTag } from '../../types';

const PURCHASE_SOURCES = ['Amazon', 'Walmart', 'Target', 'Costco', 'eBay', 'In-store', 'Brand website', 'Other'];

const CURRENCIES = [
  { code: 'USD', symbol: '$' }, { code: 'EUR', symbol: '€' }, { code: 'GBP', symbol: '£' },
  { code: 'CAD', symbol: 'C$' }, { code: 'AUD', symbol: 'A$' }, { code: 'JPY', symbol: '¥' },
  { code: 'INR', symbol: '₹' }, { code: 'BRL', symbol: 'R$' }, { code: 'MXN', symbol: 'MX$' },
  { code: 'KRW', symbol: '₩' }, { code: 'CNY', symbol: '¥' }, { code: 'CHF', symbol: 'Fr' },
];

const SCALE_COLORS = ['red', 'yellow', 'teal'] as const;

function ScaleSelector({
  group,
  tags,
  value,
  onChange,
}: {
  group: string;
  tags: ReviewTag[];
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  return (
    <Box>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={6}>{group}</Text>
      <Group gap={0} style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--mantine-color-default-border)' }}>
        {tags.map((tag, i) => {
          const selected = value === String(tag.id);
          const color = SCALE_COLORS[i] ?? 'gray';
          return (
            <UnstyledButton
              key={tag.id}
              onClick={() => onChange(selected ? null : String(tag.id))}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '10px 8px',
                borderLeft: i > 0 ? '1px solid var(--mantine-color-default-border)' : undefined,
                backgroundColor: selected
                  ? `var(--mantine-color-${color}-light)`
                  : `var(--mantine-color-gray-0)`,
                transition: 'background-color 120ms',
              }}
            >
              <Text size="sm" c={selected ? 'dark' : 'dimmed'} fw={selected ? 600 : 400}>
                {tag.emoji} {tag.label}
              </Text>
            </UnstyledButton>
          );
        })}
      </Group>
    </Box>
  );
}

function VisibilityLabel({ title, sub }: { title: string; sub: string }) {
  return (
    <Stack gap={0} align="center">
      <Text size="sm" fw={600} lh={1.3}>{title}</Text>
      <Text size="xs" c="dimmed" lh={1.3}>{sub}</Text>
    </Stack>
  );
}

export default function EditReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Form fields
  const [rating, setRating] = useState(0);
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState<string>(String(ReviewVisibility.Public));
  const [valueTagId, setValueTagId] = useState<string | null>(null);
  const [qualityTagId, setQualityTagId] = useState<string | null>(null);
  const [experienceTagId, setExperienceTagId] = useState<string | null>(null);
  const [purchaseSource, setPurchaseSource] = useState<string | null>(null);
  const [customSource, setCustomSource] = useState('');
  const [purchasePrice, setPurchasePrice] = useState<number | ''>('');
  const [purchaseCurrency, setPurchaseCurrency] = useState('USD');
  const [isPromotional, setIsPromotional] = useState(false);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [newImageItems, setNewImageItems] = useState<{ file: File; preview: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formInitialized, setFormInitialized] = useState(false);

  const { data: review, isLoading, error } = useQuery({
    queryKey: ['review-edit', id],
    queryFn: () => reviewsService.getById(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['review-tags'],
    queryFn: () => tagsService.getAll(),
    staleTime: Infinity,
  });

  const tagGroups = allTags.reduce<Record<string, typeof allTags>>((acc, tag) => {
    if (!acc[tag.group]) acc[tag.group] = [];
    acc[tag.group].push(tag);
    return acc;
  }, {});

  // Pre-populate form once review and tags load
  useEffect(() => {
    if (review && allTags.length > 0 && !formInitialized) {
      setRating(review.rating);
      setContent(review.content);
      setVisibility(String(review.visibility ?? ReviewVisibility.Public));
      setExistingImageUrls(review.imageUrls ?? []);
      const src = review.purchaseSource ?? null;
      if (src && !PURCHASE_SOURCES.includes(src)) {
        setPurchaseSource('Other');
        setCustomSource(src);
      } else {
        setPurchaseSource(src);
      }

      setPurchasePrice(review.purchasePrice ?? '');
      setPurchaseCurrency(review.purchaseCurrency ?? 'USD');
      setIsPromotional(review.isPromotional ?? false);

      // Map existing tags back to group state
      for (const tag of review.tags ?? []) {
        const group = allTags.find((t) => t.id === tag.id)?.group;
        if (group === 'Value') setValueTagId(String(tag.id));
        else if (group === 'Quality') setQualityTagId(String(tag.id));
        else if (group === 'Experience') setExperienceTagId(String(tag.id));
      }

      setFormInitialized(true);
    }
  }, [review, allTags, formInitialized]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const rid = review!.id;

      // Remove any images the user deleted
      const removedUrls = (review!.imageUrls ?? []).filter((url) => !existingImageUrls.includes(url));
      for (const url of removedUrls) {
        await reviewsService.removeImage(rid, url);
      }

      // Upload new images if any
      if (newImageItems.length > 0) {
        await reviewsService.uploadImages(rid, newImageItems.map((i) => i.file));
      }

      return reviewsService.update(rid, {
        rating,
        content: content.trim(),
        visibility: Number(visibility) as ReviewVisibility,
        tagIds: [valueTagId, qualityTagId, experienceTagId].filter(Boolean).map(Number),
        purchaseSource: purchaseSource === 'Other' ? (customSource.trim() || null) : (purchaseSource || null),
        purchasePrice: purchasePrice === '' ? null : purchasePrice,
        purchaseCurrency: purchasePrice !== '' ? purchaseCurrency : undefined,
        isPromotional,
      });
    },
    onSuccess: () => {
      notifications.show({ title: 'Saved', message: 'Review updated successfully', color: 'green' });
      navigate(`/reviews/${review!.id}`);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update review', color: 'red' });
    },
  });

  const handleSave = () => {
    if (!review) return;
    if (rating === 0) {
      notifications.show({ title: 'Error', message: 'Please select a rating', color: 'red' });
      return;
    }
    if (!content.trim()) {
      notifications.show({ title: 'Error', message: 'Please enter review content', color: 'red' });
      return;
    }
    saveMutation.mutate();
  };

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !review) {
    return (
      <Container size="sm" pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {error ? 'Failed to load review' : 'Review not found'}
          <Button variant="subtle" size="xs" mt="xs" onClick={() => navigate(-1)}>Go back</Button>
        </Alert>
      </Container>
    );
  }

  if (user?.id !== review.user.id) {
    return (
      <Container size="sm" pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Unauthorized">
          You can only edit your own reviews.
          <Button variant="subtle" size="xs" mt="xs" onClick={() => navigate(-1)}>Go back</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="sm" pt="md" pb="xl">
      <Title order={2} mb="lg">Edit Review</Title>

      {/* Product info */}
      {review.product && (
        <Card shadow="sm" padding="md" radius="md" withBorder mb="lg">
          <Group gap="sm">
            {review.product.imageUrl && (
              <Image
                src={review.product.imageUrl}
                alt={review.product.name}
                w={60}
                h={60}
                radius="sm"
                fit="contain"
              />
            )}
            <div>
              <Text fw={500}>{review.product.name}</Text>
              {review.product.brand && <Text size="sm" c="dimmed">{review.product.brand}</Text>}
            </div>
          </Group>
        </Card>
      )}

      <Stack gap="md">
        <div>
          <Text size="sm" fw={500} mb="xs">
            Rating <span style={{ color: 'var(--mantine-color-red-6)' }}>*</span>
          </Text>
          <StarRating rating={rating} onChange={setRating} readonly={false} size="lg" />
        </div>

        <HashtagTextarea
          value={content}
          onChange={setContent}
          placeholder="Share your thoughts about this product..."
          required
          minRows={5}
          maxRows={15}
          maxLength={5000}
        />

        {/* Image management */}
        <div>
          <Text size="sm" fw={500} mb="xs">
            Photos <Text span c="dimmed" size="xs">(optional, up to 5)</Text>
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.currentTarget.files ?? []);
              const totalExisting = existingImageUrls.length + newImageItems.length;
              const remaining = 5 - totalExisting;
              const added = files.slice(0, remaining).map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
              setNewImageItems((prev) => [...prev, ...added]);
              e.currentTarget.value = '';
            }}
          />
          <Group gap="xs" wrap="wrap">
            {/* Existing images with remove */}
            {existingImageUrls.map((url) => (
              <div key={url} style={{ position: 'relative' }}>
                <img
                  src={url}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--mantine-color-default-border)', display: 'block' }}
                  alt=""
                />
                <ActionIcon
                  size="xs"
                  color="red"
                  variant="filled"
                  radius="xl"
                  style={{ position: 'absolute', top: -6, right: -6 }}
                  onClick={() => setExistingImageUrls((prev) => prev.filter((u) => u !== url))}
                >
                  <IconX size={10} />
                </ActionIcon>
              </div>
            ))}
            {/* New images with remove */}
            {newImageItems.map((item, i) => (
              <div key={item.preview} style={{ position: 'relative' }}>
                <img
                  src={item.preview}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--mantine-color-default-border)', display: 'block' }}
                  alt=""
                />
                <ActionIcon
                  size="xs"
                  color="red"
                  variant="filled"
                  radius="xl"
                  style={{ position: 'absolute', top: -6, right: -6 }}
                  onClick={() => {
                    URL.revokeObjectURL(item.preview);
                    setNewImageItems((prev) => prev.filter((_, idx) => idx !== i));
                  }}
                >
                  <IconX size={10} />
                </ActionIcon>
              </div>
            ))}
            {/* Add more button */}
            {existingImageUrls.length + newImageItems.length < 5 && (
              <UnstyledButton
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: 80, height: 80, borderRadius: 8,
                  border: '2px dashed var(--mantine-color-default-border)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                  color: 'var(--mantine-color-dimmed)',
                  transition: 'border-color 150ms, color 150ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--mantine-color-brand-filled)'; e.currentTarget.style.color = 'var(--mantine-color-brand-filled)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--mantine-color-default-border)'; e.currentTarget.style.color = 'var(--mantine-color-dimmed)'; }}
              >
                <IconPhoto size={20} />
                <Text size="xs">Add photo</Text>
              </UnstyledButton>
            )}
          </Group>
        </div>

        <Divider label="Optional details" labelPosition="left" />

        {/* Sub-ratings */}
        {allTags.length > 0 && (
          <Stack gap="sm">
            <Text size="sm" fw={500}>Quick ratings <Text span c="dimmed" size="xs">(optional)</Text></Text>
            <ScaleSelector group="Value"      tags={tagGroups['Value']      ?? []} value={valueTagId}      onChange={setValueTagId} />
            <ScaleSelector group="Quality"    tags={tagGroups['Quality']    ?? []} value={qualityTagId}    onChange={setQualityTagId} />
            <ScaleSelector group="Experience" tags={tagGroups['Experience'] ?? []} value={experienceTagId} onChange={setExperienceTagId} />
          </Stack>
        )}

        {/* Purchase info */}
        <Stack gap="xs">
          <Select
            label="Where did you buy it?"
            placeholder="Select retailer (optional)"
            value={purchaseSource}
            onChange={(v) => { setPurchaseSource(v); if (v !== 'Other') setCustomSource(''); }}
            clearable
            data={PURCHASE_SOURCES}
          />
          {purchaseSource === 'Other' && (
            <TextInput
              placeholder="Enter retailer name"
              value={customSource}
              onChange={(e) => setCustomSource(e.currentTarget.value)}
              maxLength={50}
            />
          )}
          <div>
            <Text size="sm" fw={500} mb={4}>
              What did you pay? <Text span size="sm" c="dimmed">(optional)</Text>
            </Text>
            <Group gap={4} wrap="nowrap" align="flex-start">
              <NativeSelect
                value={purchaseCurrency}
                onChange={(e) => setPurchaseCurrency(e.currentTarget.value)}
                data={CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} ${c.symbol}` }))}
                style={{ width: 100, flexShrink: 0 }}
              />
              <NumberInput
                style={{ flex: 1 }}
                placeholder="0.00"
                value={purchasePrice}
                onChange={(val) => setPurchasePrice(val === '' ? '' : Number(val))}
                decimalScale={2}
                min={0}
                max={99999}
              />
            </Group>
          </div>
        </Stack>

        <Checkbox
          label="I received this product for free or at a discount in exchange for my review"
          checked={isPromotional}
          onChange={(e) => setIsPromotional(e.currentTarget.checked)}
        />

        <div>
          <Text size="sm" fw={500} mb={6}>Visibility</Text>
          <SegmentedControl
            fullWidth
            value={visibility}
            onChange={setVisibility}
            data={[
              { value: String(ReviewVisibility.Public),        label: <VisibilityLabel title="Public"    sub="Everyone" /> },
              { value: String(ReviewVisibility.FollowersOnly), label: <VisibilityLabel title="Followers" sub="Followers only" /> },
              { value: String(ReviewVisibility.Private),       label: <VisibilityLabel title="Private"   sub="Only you" /> },
            ]}
          />
        </div>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={() => navigate(-1)}>Cancel</Button>
          <Button onClick={handleSave} loading={saveMutation.isPending}>Save Changes</Button>
        </Group>
      </Stack>
    </Container>
  );
}
